import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import {
  ArrowRight, ChevronLeft, ChevronRight, Quote, Truck, ShieldCheck, IndianRupee, Headset,
  Star, Sparkles, Award, Heart, Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck,
  ShoppingBag, Phone, MapPin, Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves,
} from "lucide-react";
import ProductCard from "./ProductCard";
import SeoMeta from "@/components/SeoMeta";
import AbpHomeRail from "./AbpHomeRail";
import { useStoreBase } from "@/lib/store";
import { useStoreHomeFeed } from "./useStoreHomeFeed";
import { isWhyUsEmoji } from "@/lib/whyUsIcons";
import { useTranslation } from "react-i18next";
import TikTokCommerce from "./TikTokCommerce";
import { slideCtaHref } from "./StorefrontNav";
import { slideCtaButtonText, slideFullBannerClick, slideHasText, slideHeroHref } from "@/lib/heroSlide";
import "./storefrontCosmos.css";

const COSMOS_BLUE = "#2563EB";
const COSMOS_INK = "#0F172A";

const ICONS = {
  Truck, ShieldCheck, IndianRupee, Headset, Star, Sparkles, Award, Heart,
  Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck, ShoppingBag, Phone, MapPin,
  Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves,
};
function IconByName({ name, className }) {
  if (isWhyUsEmoji(name)) return <span className="text-xl leading-none" aria-hidden>{name}</span>;
  const C = ICONS[name] || Star;
  return <C className={className} />;
}

function CosmosHero({ config, primary }) {
  const { base } = useStoreBase();
  const slidesFromCfg = (config.homepage?.slides || []).filter((s) => s && (s.image_url || s.image_url_mobile || s.title));
  const slides = slidesFromCfg.length > 0
    ? slidesFromCfg
    : [{
        image_url: config.homepage?.hero_image_url || null,
        image_url_mobile: config.homepage?.hero_image_url_mobile || null,
        title: config.homepage?.hero_title,
        subtitle: config.homepage?.hero_subtitle,
        cta_text: config.homepage?.hero_cta_text || "Shop Now",
        cta_link: "#shop",
      }].filter((s) => s.image_url || s.image_url_mobile || s.title);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length, paused]);
  if (slides.length === 0) {
    return <section className="w-full min-h-[52vh] sm:min-h-[68vh] bg-sky-100" data-testid="cosmos-hero" style={{ background: `linear-gradient(90deg, ${primary}33, ${primary})` }} />;
  }
  const go = (dir) => setIdx((i) => (i + dir + slides.length) % slides.length);
  const s = slides[idx];
  const overlayOn = slideHasText(s);
  const href = slideHeroHref(base, s, slideCtaHref);
  const ctaText = slideCtaButtonText(s);
  return (
    <section className="relative w-full overflow-hidden bg-sky-50" data-testid="cosmos-hero">
      <div className="relative w-full aspect-[4/5] sm:aspect-[1920/560] max-h-[88vh] min-h-[52vh]">
        {slides.map((sl, i) => {
          const slideHref = slideHeroHref(base, sl, slideCtaHref);
          const slideFull = slideFullBannerClick(sl);
          const cls = `absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`;
          const body = (sl.image_url || sl.image_url_mobile) ? (
            <>
              <img src={mediaUrl(sl.image_url || sl.image_url_mobile)} alt="" className="hidden sm:block w-full h-full object-cover" />
              <img src={mediaUrl(sl.image_url_mobile || sl.image_url)} alt="" className="sm:hidden w-full h-full object-cover" />
            </>
          ) : (
            <div className="w-full h-full" style={{ background: primary }} />
          );
          return slideFull && slideHref ? (
            <a key={i} href={slideHref} className={cls} data-testid={`hero-slide-link-${i}`}>{body}</a>
          ) : (
            <div key={i} className={cls}>{body}</div>
          );
        })}
        {overlayOn ? (
          <>
            <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/55 via-black/20 to-transparent pointer-events-none" data-testid="cosmos-hero-overlay" />
            <div className="absolute inset-0 z-10 pointer-events-none flex items-center">
              <div className="w-full max-w-[90rem] mx-auto px-5 sm:px-10 lg:px-14">
                <div className="max-w-xl text-white text-left">
                  {s.title ? <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">{s.title}</h1> : null}
                  {s.subtitle ? <p className="mt-3 text-sm sm:text-lg text-white/90 max-w-lg">{s.subtitle}</p> : null}
                  {href && ctaText ? (
                    <div className="mt-6">
                      <a href={href} className="pointer-events-auto inline-flex items-center rounded-full px-6 py-2.5 text-sm font-bold text-white" style={{ background: primary }} data-testid="cosmos-hero-cta">
                        {ctaText}
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        ) : null}
        {slides.length > 1 && (
          <>
            <button type="button" aria-label="Previous slide" onClick={() => go(-1)} className="absolute z-20 left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-900 flex items-center justify-center shadow" data-testid="hero-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button type="button" aria-label="Next slide" onClick={() => go(1)} className="absolute z-20 right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 text-stone-900 flex items-center justify-center shadow" data-testid="hero-next">
              <ChevronRight className="w-5 h-5" />
            </button>
            <button type="button" aria-label={paused ? "Play slides" : "Pause slides"} onClick={() => setPaused((v) => !v)} className="absolute z-20 right-4 bottom-4 w-8 h-8 rounded-full bg-black/50 text-white text-xs font-bold" data-testid="cosmos-hero-pause">
              {paused ? "▶" : "❚❚"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function CosmosCategories({ categories, primary }) {
  const { base } = useStoreBase();
  const roots = (categories || []).filter((c) => !c.parent_id);
  if (!roots.length) return null;
  const loop = roots.length > 1 ? [...roots, ...roots] : roots;
  return (
    <section id="categories" className="py-10 sm:py-14 overflow-hidden" data-testid="cosmos-categories">
      <div className="cosmos-marquee gap-8 sm:gap-12 px-6" style={{ animationDuration: `${Math.max(22, roots.length * 6)}s` }} data-testid="cosmos-cat-track">
        {loop.map((c, i) => (
          <Link key={`${c.id}-${i}`} to={`${base}/shop?category=${c.slug}`} className="shrink-0 w-[7.5rem] sm:w-36 flex flex-col items-center" data-testid={`cat-tile-${c.slug}`}>
            <div className="relative w-[7.2rem] h-[7.2rem] sm:w-32 sm:h-32 flex items-end justify-center">
              <span className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-7 rounded-[100%] opacity-40" style={{ background: `radial-gradient(ellipse at center, ${primary}aa, ${primary}22 70%, transparent)` }} />
              {c.image_url ? (
                <img src={mediaUrl(c.image_url)} alt={c.name} className="relative z-10 max-h-[6.4rem] w-auto object-contain shadow-none" />
              ) : (
                <span className="relative z-10 w-20 h-20 rounded-full flex items-center justify-center font-extrabold text-white" style={{ background: primary }}>{c.name.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="mt-2 text-sm font-semibold text-stone-800 text-center line-clamp-1 w-full">{c.name}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function CosmosExplore({ title, products, categories, slug, testid }) {
  const { base } = useStoreBase();
  const [chip, setChip] = useState("best");
  const roots = (categories || []).filter((c) => !c.parent_id).slice(0, 6);
  const filtered = useMemo(() => {
    if (chip === "all") return products;
    if (chip === "best") return products.filter((p) => p.is_featured).concat(products).filter((p, i, a) => a.findIndex((x) => x.id === p.id) === i);
    return products.filter((p) => p.category_id === chip || (p.category_ids || []).includes(chip) || p.category_slug === chip);
  }, [chip, products]);
  const list = filtered.length ? filtered : products;
  const loop = list.length > 1 ? [...list, ...list] : list;
  const scroller = useRef(null);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const tick = () => {
      const el = scroller.current;
      if (!el) return;
      const card = el.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 16 : 280;
      const half = el.scrollWidth / 2;
      const next = el.scrollLeft + step;
      if (next >= half - 4) el.scrollLeft = next - half;
      else el.scrollTo({ left: next, behavior: "smooth" });
    };
    const t = setInterval(tick, 3400);
    return () => clearInterval(t);
  }, [list.length, chip]);
  const go = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 16 : 280;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };
  if (!products.length) return null;
  return (
    <section className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid={testid}>
      <h2 className="text-center font-display font-extrabold text-2xl sm:text-4xl text-stone-900">{title}</h2>
      <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 justify-center" data-testid={`${testid}-chips`}>
        <button type="button" onClick={() => setChip("best")} className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold border ${chip === "best" ? "text-white border-transparent" : "bg-white text-stone-600 border-stone-200"}`} style={chip === "best" ? { background: "var(--store-primary, #2563EB)" } : undefined}>Best Sellers</button>
        {roots.map((c) => (
          <button key={c.id} type="button" onClick={() => setChip(c.id)} className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold border ${chip === c.id ? "text-white border-transparent" : "bg-white text-stone-600 border-stone-200"}`} style={chip === c.id ? { background: "var(--store-primary, #2563EB)" } : undefined}>{c.name}</button>
        ))}
        <button type="button" onClick={() => setChip("all")} className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold border ${chip === "all" ? "text-white border-transparent" : "bg-white text-stone-600 border-stone-200"}`} style={chip === "all" ? { background: "var(--store-primary, #2563EB)" } : undefined}>All</button>
        <div className="hidden md:flex items-center gap-1 ml-2">
          <button type="button" aria-label="Previous" onClick={() => go(-1)} className="w-9 h-9 rounded-full border border-sky-200 text-sky-600 inline-flex items-center justify-center" data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" aria-label="Next" onClick={() => go(1)} className="w-9 h-9 rounded-full border border-sky-200 text-sky-600 inline-flex items-center justify-center" data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div ref={scroller} className="mt-6 flex gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory pb-2" data-testid={`${testid}-scroller`}>
        {loop.map((p, i) => (
          <div key={`${p.id}-${i}`} className="snap-start shrink-0 w-[78%] sm:w-[46%] md:w-[31%] lg:w-[23%]">
            <ProductCard p={p} slug={slug} variant="cosmos" />
          </div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link to={`${base}/shop`} className="text-sm font-semibold" style={{ color: "var(--store-primary, #2563EB)" }} data-testid={`${testid}-viewall`}>View all</Link>
      </div>
    </section>
  );
}

function CosmosLoopSlider({ title, products, slug, testid }) {
  const { base } = useStoreBase();
  const scroller = useRef(null);
  const loop = products.length > 1 ? [...products, ...products] : products;
  useEffect(() => {
    if (products.length < 2) return undefined;
    const tick = () => {
      const el = scroller.current;
      if (!el) return;
      const card = el.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 16 : 280;
      const half = el.scrollWidth / 2;
      const next = el.scrollLeft + step;
      if (next >= half - 4) el.scrollLeft = next - half;
      else el.scrollTo({ left: next, behavior: "smooth" });
    };
    const t = setInterval(tick, 3600);
    return () => clearInterval(t);
  }, [products.length]);
  if (!products.length) return null;
  return (
    <section className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12" data-testid={testid}>
      <h2 className="text-center font-display font-extrabold text-2xl sm:text-4xl text-stone-900">{title}</h2>
      <div ref={scroller} className="mt-8 flex gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory pb-2" data-testid={`${testid}-scroller`}>
        {loop.map((p, i) => (
          <div key={`${p.id}-${i}`} className="snap-start shrink-0 w-[78%] sm:w-[46%] md:w-[31%] lg:w-[23%]">
            <ProductCard p={p} slug={slug} variant="cosmos" />
          </div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link to={`${base}/shop`} className="text-sm font-semibold" style={{ color: "var(--store-primary, #2563EB)" }}>View all</Link>
      </div>
    </section>
  );
}

function CosmosBanners({ banners, primary, secondary }) {
  const list = (banners || []).filter((b) => b && (b.image_url || b.title));
  if (!list.length) return null;
  return (
    <section className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="cosmos-side-banners">
      <div className={`grid gap-4 sm:gap-6 ${list.length > 1 ? "md:grid-cols-2" : ""}`}>
        {list.slice(0, 2).map((b, i) => (
          <a key={i} href={b.cta_link || "#shop"} className="group relative block rounded-3xl overflow-hidden min-h-[220px] sm:min-h-[280px]" data-testid={`featured-banner-${i}`}>
            {b.image_url ? <img src={mediaUrl(b.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="absolute inset-0" style={{ background: i ? secondary : primary }} />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 text-white">
              {b.title ? <h3 className="font-display text-2xl font-extrabold">{b.title}</h3> : null}
              {b.subtitle ? <p className="mt-1 text-sm opacity-90">{b.subtitle}</p> : null}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function CosmosPromo({ data }) {
  if (!data?.image_url && !data?.image_url_mobile) return null;
  const inner = (
    <>
      {data.image_url ? <img src={mediaUrl(data.image_url)} alt="" className="hidden sm:block absolute inset-0 w-full h-full object-cover" /> : null}
      {(data.image_url_mobile || data.image_url) ? <img src={mediaUrl(data.image_url_mobile || data.image_url)} alt="" className="sm:hidden absolute inset-0 w-full h-full object-cover" /> : null}
    </>
  );
  return (
    <section className="w-full" data-testid="cosmos-promo-banner">
      <div className="relative w-full min-h-[280px] sm:min-h-[420px] lg:min-h-[520px] overflow-hidden">
        {data.cta_link ? <a href={data.cta_link} className="absolute inset-0" data-testid="cosmos-promo-link">{inner}</a> : inner}
      </div>
    </section>
  );
}

function CosmosTags({ tags }) {
  const { base } = useStoreBase();
  const list = (tags || []).slice(0, 8);
  if (!list.length) return null;
  return (
    <section className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="cosmos-tags">
      <h2 className="text-center font-display font-extrabold text-2xl sm:text-4xl text-stone-900">Shop By Concerns</h2>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 cosmos-tag-grid">
        {list.map((tg) => (
          <Link key={tg.id} to={`${base}/shop?tag=${tg.slug}`} className="group" data-testid={`tag-tile-${tg.slug}`}>
            <div className="rounded-2xl overflow-hidden bg-stone-100 aspect-[5/4]">
              {tg.image_url ? <img src={mediaUrl(tg.image_url)} alt={tg.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center font-extrabold text-stone-400">{tg.name.slice(0, 1)}</div>}
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 font-bold text-stone-900">
              {tg.name} <ArrowRight className="w-4 h-4 text-stone-400" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function CosmosTestimonials({ items, primary }) {
  const scroller = useRef(null);
  const list = (items || []).filter((t) => t && (t.review || t.name));
  const loop = list.length > 1 ? [...list, ...list] : list;
  if (!list.length) return null;
  const go = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: "smooth" });
  };
  return (
    <section className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="cosmos-testimonials">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display font-extrabold text-2xl sm:text-4xl text-stone-900">What our customers say</h2>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous" onClick={() => go(-1)} className="w-9 h-9 rounded-full border border-stone-200 inline-flex items-center justify-center" data-testid="cosmos-testi-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" aria-label="Next" onClick={() => go(1)} className="w-9 h-9 rounded-full border border-stone-200 inline-flex items-center justify-center" data-testid="cosmos-testi-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div ref={scroller} className="mt-8 flex gap-4 overflow-x-auto no-scrollbar scroll-smooth snap-x pb-2" data-testid="cosmos-testi-scroller">
        {loop.map((t, i) => (
          <div key={i} className="snap-start shrink-0 w-[78%] sm:w-[46%] lg:w-[22%] rounded-2xl border border-stone-100 bg-white overflow-hidden" data-testid={`cosmos-testi-${i}`}>
            <div className="aspect-square bg-stone-100">
              {t.image_url ? <img src={mediaUrl(t.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center" style={{ color: primary }}><Quote className="w-10 h-10" /></div>}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-3.5 h-3.5 text-amber-400" fill={n <= (t.rating || 5) ? "#fbbf24" : "none"} />)}
                <span className="text-xs text-stone-500 ml-1">{Number(t.rating || 5).toFixed(1)}</span>
              </div>
              <p className="mt-2 text-sm font-semibold text-stone-900 line-clamp-2">{t.name}</p>
              <p className="mt-1 text-sm text-stone-600 line-clamp-4">{t.review}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CosmosWhy({ items, heading, subheading, primary }) {
  const list = (items || []).filter((w) => w && (w.title || w.description));
  if (!list.length) return null;
  return (
    <section className="bg-[#F6F8FC] py-12 sm:py-16" data-testid="why-choose-us">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: primary }}>{heading || "Why choose us"}</p>
        <h2 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-stone-900">{subheading || "Loved by shoppers, backed by trust."}</h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
          {list.map((w, i) => (
            <div key={i} className="rounded-2xl bg-white p-5 border border-stone-100" data-testid={`cosmos-why-${i}`}>
              <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center" style={{ background: `${primary}18`, color: primary }}><IconByName name={w.icon} className="w-5 h-5" /></div>
              <div className="mt-3 font-bold text-stone-900">{w.title}</div>
              {w.description ? <p className="mt-1 text-sm text-stone-500">{w.description}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CosmosNewsletter({ config }) {
  const { slug } = useStoreBase();
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const hp = config?.homepage || {};
  const desktop = hp.newsletter_image_url;
  const mobile = hp.newsletter_image_url_mobile || desktop;
  const primary = config.branding?.primary_color || COSMOS_BLUE;
  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    try {
      await api.post(`/store/${slug}/newsletter`, { email: v });
      setOk(true);
      setEmail("");
    } catch { setOk(false); }
  };
  return (
    <section className="relative overflow-hidden min-h-[220px] sm:min-h-[280px]" data-testid="cosmos-newsletter">
      {desktop ? <img src={mediaUrl(desktop)} alt="" className="hidden sm:block absolute inset-0 w-full h-full object-cover" /> : null}
      {mobile ? <img src={mediaUrl(mobile)} alt="" className="sm:hidden absolute inset-0 w-full h-full object-cover" /> : null}
      {!desktop && !mobile ? <div className="absolute inset-0 bg-gradient-to-r from-sky-200 via-white to-sky-100" /> : null}
      <div className="absolute inset-0 bg-white/25" />
      <div className="relative max-w-3xl mx-auto px-4 py-14 sm:py-20 text-center">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-stone-900">Sign up and save</h2>
        <p className="mt-2 text-stone-600">Subscribe to our mailing list to be the first to know about offers.</p>
        <form onSubmit={submit} className="mt-6 mx-auto max-w-lg flex rounded-full overflow-hidden border border-stone-200 bg-white shadow-sm">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Enter your email" className="flex-1 h-12 px-5 text-sm outline-none" data-testid="cosmos-newsletter-input" />
          <button type="submit" className="h-12 px-5 text-sm font-bold text-white shrink-0" style={{ background: primary }} data-testid="cosmos-newsletter-submit">Subscribe→</button>
        </form>
        {ok ? <p className="mt-3 text-sm text-emerald-700">Thanks for subscribing.</p> : null}
      </div>
    </section>
  );
}

export default function StorefrontHomeCosmos({ config }) {
  const { t } = useTranslation();
  const { slug, base } = useStoreBase();
  const { categories, featured, recent, tags } = useStoreHomeFeed({ withTags: true });

  const primary = config.branding?.primary_color || COSMOS_BLUE;
  const secondary = config.branding?.secondary_color || COSMOS_INK;
  const hp = config.homepage || {};
  const popular = [...recent].sort((a, b) => Number(b.rating_count || 0) - Number(a.rating_count || 0)).slice(0, 12);
  const featuredPool = featured.length ? featured : recent;
  const featuredBanners = (hp.featured_banners && hp.featured_banners.length > 0)
    ? hp.featured_banners
    : (hp.featured_banner ? [hp.featured_banner] : []);
  const allShown = recent.slice(0, 8);
  const exploreTitle = `Explore ${config.store_info?.name || "the shop"}`;

  return (
    <div className="bg-white" data-testid="store-home-cosmos">
      <SeoMeta endpoint={`/seo/${slug}/meta`} fallback={{ title: (config.store_info?.name || slug), description: hp.hero_subtitle || "" }} />
      <CosmosHero config={config} primary={primary} />
      <AbpHomeRail config={config} />
      {hp.show_categories && <CosmosCategories categories={categories} primary={primary} />}
      {hp.show_featured && <CosmosExplore title={exploreTitle} products={featuredPool} categories={categories} slug={slug} testid="cosmos-featured" />}
      <CosmosBanners banners={featuredBanners} primary={primary} secondary={secondary} />
      {hp.show_popular !== false && <CosmosLoopSlider title="Popular right now" products={popular.length ? popular : recent} slug={slug} testid="cosmos-popular" />}
      <CosmosTags tags={tags} />
      <TikTokCommerce homepage={{ ...hp, tiktok_commerce: { ...(hp.tiktok_commerce || {}), title: hp.tiktok_commerce?.title || "Real People Real Results" } }} primary={primary} layout="cosmos" />
      <CosmosPromo data={hp.promo_banner} />
      <section id="shop" className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="cosmos-all-products">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-stone-900">{t("store.allProducts")}</h2>
          <Link to={`${base}/shop`} className="text-sm font-semibold" style={{ color: primary }} data-testid="home-shop-viewall">{t("store.viewAllProducts")}</Link>
        </div>
        {allShown.length === 0 ? (
          <div className="text-stone-500 text-sm">This store doesn&#39;t have any products yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {allShown.map((p) => (<ProductCard key={p.id} p={p} slug={slug} variant="cosmos" />))}
          </div>
        )}
      </section>
      {hp.show_testimonials !== false && <CosmosTestimonials items={hp.testimonials} primary={primary} />}
      {hp.show_why_us && <CosmosWhy items={hp.why_us} heading={hp.why_us_heading} subheading={hp.why_us_subheading} primary={primary} />}
    </div>
  );
}
